import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatMonth(month: string) {
  const date = new Date(month);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
  });
}

export function formatNumber(
  value: number,
  minimumFractionDigits = 0,
  maximumFractionDigits = 0
) {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(value);
}

export function formatCurrency(value: number, currency: string) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value / 100);
}

// Helper function to format currency with Intl
function formatCurrencyWithSuffix(
  value: number,
  currency: string,
  suffix: string,
  maxFractionDigits: number
) {
  return (
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: maxFractionDigits,
    }).format(value) + suffix
  );
}

// 1000 -> 1K, 1000000 -> 1M
export function numberToHumanReadable(value: number, currency?: string) {
  value = value / 100;

  if (value >= 1000000000) {
    const formatted = `${(value / 1000000000).toFixed(1)}B`;
    return currency
      ? formatCurrencyWithSuffix(value / 1000000000, currency, 'B', 1)
      : formatted;
  } else if (value >= 1000000) {
    const formatted = `${(value / 1000000).toFixed(1)}M`;
    return currency
      ? formatCurrencyWithSuffix(value / 1000000, currency, 'M', 1)
      : formatted;
  } else if (value >= 1000) {
    const formatted = `${(value / 1000).toFixed(1)}K`;
    return currency
      ? formatCurrencyWithSuffix(value / 1000, currency, 'K', 1)
      : formatted;
  } else {
    const formatted = `${value.toFixed(0)}`;
    return currency
      ? formatCurrencyWithSuffix(value, currency, '', 0)
      : formatted;
  }
}

// Shorter version for Y-axis labels to prevent wrapping
export function numberToHumanReadableShort(value: number, currency?: string) {
  value = value / 100;

  if (value >= 1000000000) {
    const formatted = `${(value / 1000000000).toFixed(0)}B`;
    return currency
      ? formatCurrencyWithSuffix(value / 1000000000, currency, 'B', 0)
      : formatted;
  } else if (value >= 1000000) {
    const formatted = `${(value / 1000000).toFixed(0)}M`;
    return currency
      ? formatCurrencyWithSuffix(value / 1000000, currency, 'M', 0)
      : formatted;
  } else if (value >= 1000) {
    const formatted = `${(value / 1000).toFixed(0)}K`;
    return currency
      ? formatCurrencyWithSuffix(value / 1000, currency, 'K', 0)
      : formatted;
  } else {
    const formatted = `${value.toFixed(0)}`;
    return currency
      ? formatCurrencyWithSuffix(value, currency, '', 0)
      : formatted;
  }
}
